/**
 * normalizeChatMessage.js
 * ──────────────────────────────────────────────────────────────────────────────
 * Central normalizer that converts ANY raw API / WebSocket / optimistic message
 * into one safe, primitive-only shape before it ever enters React state.
 *
 * This is the SINGLE fix point for "Objects are not valid as a React child".
 * Apply at every boundary where messages enter state:
 *   - API fetch (openThread)
 *   - WebSocket CHAT_MESSAGE_RECEIVED
 *   - Optimistic local send
 *   - System / reassignment injected messages
 */

/**
 * Safely extract a string value from any content field.
 * @param {*} raw - any value
 * @returns {string}
 */
const extractContent = (raw) => {
    if (raw === null || raw === undefined) return '';
    if (typeof raw === 'string') return raw;
    if (typeof raw === 'number' || typeof raw === 'boolean') return String(raw);
    if (Array.isArray(raw)) return raw.map(extractContent).join(', ');
    if (typeof raw === 'object') {
        // Try well-known text fields first
        const candidate =
            raw.body ||
            raw.content ||
            raw.text ||
            raw.message ||
            raw.value ||
            raw.title ||
            null;
        if (candidate !== null && typeof candidate !== 'object') return String(candidate);
        if (candidate !== null) return extractContent(candidate); // recurse once
        // Last resort: serialise
        try { return JSON.stringify(raw); } catch { return '[object]'; }
    }
    return String(raw);
};

/**
 * Normalise a single raw message object into a safe, flat shape.
 * All string fields are guaranteed to be primitives; no objects escape.
 *
 * @param {object} raw - Raw message from API or WebSocket
 * @returns {object} Normalised message
 */
export const normalizeChatMessage = (raw) => {
    if (!raw || typeof raw !== 'object') {
        return {
            id: null,
            threadId: null,
            senderId: null,
            authorName: 'System',
            authorRole: '',
            content: extractContent(raw),
            messageKind: 'text',
            sourceEvent: null,
            isFromCurrentUser: false,
            isDeleted: false,
            createdAt: null,
            updatedAt: null,
            // Keep raw numeric kind for legacy checks
            message_kind: 0,
            // Legacy passthrough fields so existing UI that accesses raw fields still works
            _id: null,
            body: extractContent(raw),
            author_display_name: 'System',
            sender_role: '',
            created_at: null,
        };
    }

    // ── Content resolution ──────────────────────────────────────────
    let content = '';

    // 1. Check body first (used by our own API)
    if (raw.body !== undefined) {
        content = extractContent(raw.body);
    }
    // 2. Then content
    else if (raw.content !== undefined) {
        content = extractContent(raw.content);
    }
    // 3. Then text
    else if (raw.text !== undefined) {
        content = extractContent(raw.text);
    }
    // 4. Then message (some websocket payloads)
    else if (raw.message !== undefined && typeof raw.message === 'string') {
        content = raw.message;
    }
    // 5. Fall through
    else {
        content = '';
    }

    if (process.env.NODE_ENV === 'development' && typeof raw.content === 'object' && raw.content !== null) {
        console.warn('[normalizeChatMessage] Message content was object. Normalized safely.', raw);
    }

    // ── Author name ─────────────────────────────────────────────────
    const authorName =
        (typeof raw.author_display_name === 'string' ? raw.author_display_name : null) ||
        (typeof raw.authorName === 'string' ? raw.authorName : null) ||
        (typeof raw.sender_display_name === 'string' ? raw.sender_display_name : null) ||
        (typeof raw.sender_name === 'string' ? raw.sender_name : null) ||
        'System';

    // ── IDs ─────────────────────────────────────────────────────────
    const id = raw._id || raw.id || raw.message_id || null;
    const threadId = raw.thread_id || raw.threadId || null;
    const senderId = raw.sender_user_id || raw.sender_id || raw.senderId || raw.user_id || null;

    // ── Message kind: normalise to number for legacy checks ─────────
    const messageKindRaw = raw.message_kind !== undefined ? raw.message_kind : (raw.messageKind !== undefined ? raw.messageKind : 0);
    const messageKindNum = Number.isFinite(Number(messageKindRaw)) ? Number(messageKindRaw) : 0;

    const normalized = {
        // ── Canonical camelCase fields ──────────────────────────────
        id,
        threadId,
        senderId,
        authorName,
        authorRole: String(raw.author_role || raw.authorRole || raw.sender_role || raw.role || ''),
        content,
        messageKind: messageKindNum === 1 ? 'system' : 'text',
        sourceEvent: raw.source_event || raw.sourceEvent || null,
        isFromCurrentUser: Boolean(raw.is_from_current_user || raw.isFromCurrentUser),
        isDeleted: Boolean(raw.is_deleted || raw.isDeleted),
        createdAt: raw.created_at || raw.createdAt || null,
        updatedAt: raw.updated_at || raw.updatedAt || null,

        // ── Legacy snake_case aliases (so existing UI still compiles) ─
        _id: id,
        message_id: id,
        body: content,
        text: content,
        author_display_name: authorName,
        sender_display_name: authorName,
        sender_role: String(raw.author_role || raw.authorRole || raw.sender_role || raw.role || ''),
        sender_id: senderId,
        sender_user_id: senderId,
        message_kind: messageKindNum,
        is_from_current_user: Boolean(raw.is_from_current_user || raw.isFromCurrentUser),
        is_deleted: Boolean(raw.is_deleted || raw.isDeleted),
        created_at: raw.created_at || raw.createdAt || null,
        updated_at: raw.updated_at || raw.updatedAt || null,
        thread_id: threadId,
    };

    return normalized;
};

/**
 * Normalise an array of raw messages, removing any invalid entries.
 * @param {Array} rawList
 * @returns {Array}
 */
export const normalizeChatMessages = (rawList) => {
    if (!Array.isArray(rawList)) return [];
    return rawList.map(normalizeChatMessage);
};

/**
 * Deduplicate messages by their id field, keeping the latest occurrence.
 * @param {Array} messages - already normalized messages
 * @returns {Array}
 */
export const deduplicateMessages = (messages) => {
    const seen = new Map();
    for (const m of messages) {
        const key = String(m.id || m._id || m.message_id || Math.random());
        seen.set(key, m);
    }
    return Array.from(seen.values());
};
